import { darken, rgba } from 'polished';

import { get } from 'ravenjs/utils/lodash';
import { THEME } from 'ravenjs/constants/theme';

import { DEFAULT_BRANDING } from 'constants/app';
import { BRANDING } from 'constants/branding';
import { SPACING } from 'constants/spacing';
import { INFO } from 'constants/version';
import { resolveTextColorOn } from 'utils/contrast';
import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';

/**
 * Build the branding action payload
 *
 * @param   {Object}    branding
 * @return  {Object}
 */
export const buildBrandingSuccessActionPayload = branding => {
    const config = get(branding, 'data.partner-branding.brandingAttribute', {});
    const partnerName = get(branding, 'data.partner-branding.partnerName', '');
    const platformName = get(branding, 'data.partner-branding.platformName', '');
    const platformDomain = get(branding, 'data.partner-branding.platformDomain', '');
    const mainPartnerName = get(branding, 'data.partner-branding.mainPartnerName', '');
    const partnerDisplayName = get(branding, 'data.partner-branding.partnerDisplayName', '');
    const theme = buildThemeWithConfig(config);
    const { sprintTimestamp } = INFO;

    return {
        config,
        mainPartnerName,
        partnerName,
        platformName,
        platformDomain,
        sprintTimestamp,
        partnerDisplayName,
        theme,
    };
};

/**
 * Build a default theme for the branding state
 *
 * @method buildThemeWithConfig
 * @param  {Object}             [brandingConfig={}] The branding config
 * @return {Object}                           The pre-built theme
 */
export const buildThemeWithConfig = (brandingConfig = {}) => {
    const branding = { ...DEFAULT_BRANDING, ...brandingConfig };
    const topNavTextColor = resolveTextColorOn(branding.topNavColor, branding.topNavTextColor);
    const primaryTextColor = resolveTextColorOn(branding.primaryColor, branding.primaryTextColor);
    const secondaryTextColor = resolveTextColorOn(
        branding.secondaryColor,
        branding.secondaryTextColor
    );

    return {
        AppNavigation: {
            styles: {
                backgroundColor: branding.topNavColor,
            },
            height: 62,
        },
        MobileAppNavigation: {
            styles: {
                backgroundColor: branding.topNavColor,
            },
            height: 130,
        },
        Button: {
            styles: {},
        },
        ButtonSecondary: {
            styles: {},
        },
        DataView: {
            primaryText: {
                styles: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontFamily: 'Roboto',
                    color: 'inherit',
                    fontSize: '0.875rem !important',
                },
            },
        },
        Form: {
            styles: {
                form: {
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                },
            },
        },
        FormGroupTitle: {
            styles: {
                color: MIGRATED_COLORS.black,
                lineHeight: 'normal',
            },
        },
        FormGroupTitleRequired: {
            styles: {
                fontWeight: 700,
            },
        },
        NavLogo: {
            alt: branding.companyName,
            src: branding.logoUrl,
        },
        NavItem: {
            styles: {
                backgroundColor: branding.topNavColor,
                color: topNavTextColor,
                fontFamily: 'Favorit, Arial, Helvetica, sans-serif',
                fontSize: '0.6rem',
            },
            active: {
                backgroundColor: branding.topNavAccentColor,
            },
            isActive: {
                backgroundColor: MIGRATED_COLORS.brandPeach2,
            },
        },
        SubNavItem: {
            styles: {
                color: branding.secondaryTextColor,
            },
            active: {
                borderBottomColor: branding.topNavAccentColor,
            },
        },
        TableBody: {
            styles: {
                fontSize: '14px',
            },
        },
        TableCell: {
            styles: {
                head: {
                    backgroundColor: MIGRATED_COLORS.componentsTableTitleRow,
                    fontFamily: 'Roboto',
                    color: MIGRATED_COLORS.black,
                },
            },
        },
        TitleField: {
            styles: {
                root: {
                    color: 'red',
                    fontWeight: 500,
                },
            },
        },
        UserMenu: {
            styles: {
                fontFamily: 'Roboto',
                fontSize: '1rem',
            },
        },
        grid: {
            gutterWidth: 25.216,
        },
        palette: {
            primary: {
                text: primaryTextColor,
                gradientColor: branding.primaryColor,
                color: darken(0.1, branding.primaryColor),
                borderColor: branding.primaryColor,
                backgroundLightColor: rgba(branding.primaryColor, 0.3),
            },
            primaryTransparent: {
                text: branding.primaryTextColor,
                gradientColor: 'transparent',
                color: darken(0.1, branding.primaryColor),
                borderColor: 'transparent',
            },
            secondary: {
                text: secondaryTextColor,
                gradientColor: branding.secondaryColor,
                color: darken(0.1, branding.secondaryColor),
                borderColor: branding.secondaryColor,
            },
            light: {
                text: MIGRATED_COLORS.darkest,
                gradientColor: MIGRATED_COLORS.light,
                color: darken(0.1, MIGRATED_COLORS.light),
                borderColor: 'transparent',
            },
            lighter: {
                text: MIGRATED_COLORS.darkest,
                gradientColor: MIGRATED_COLORS.lighter,
                color: darken(0.1, MIGRATED_COLORS.lighter),
                borderColor: 'transparent',
            },
            activeButton: {
                text: MIGRATED_COLORS.darkest,
                gradientColor: MIGRATED_COLORS.blue,
                color: darken(0.1, MIGRATED_COLORS.lighter),
                borderColor: 'transparent',
            },
            lightOutline: {
                text: MIGRATED_COLORS.black,
                gradientColor: MIGRATED_COLORS.white,
                color: darken(0.1, MIGRATED_COLORS.white),
                borderColor: MIGRATED_COLORS.placeholder,
            },
            yellow: {
                text: MIGRATED_COLORS.black,
                gradientColor: MIGRATED_COLORS.yellow,
                color: darken(0.1, MIGRATED_COLORS.yellow),
                borderColor: MIGRATED_COLORS.dark,
            },
            tableRowStyled: {
                color: MIGRATED_COLORS.light,
            },
            tableRowStyledHover: {
                color: branding.primaryColor,
            },
            actions: {
                color: MIGRATED_COLORS.placeholder,
            },
            links: {
                color: branding.linkTextColor,
            },
            subNavActions: {
                text: branding.linkTextColor,
                color: MIGRATED_COLORS.lightest,
                gradientColor: MIGRATED_COLORS.white,
            },
            loading: {
                color: MIGRATED_COLORS.lightest,
            },
            muted: {
                color: MIGRATED_COLORS.lighter,
            },
            sidebarHelpText: {
                color: MIGRATED_COLORS.placeholder,
            },
            sidebarOption: {
                color: MIGRATED_COLORS.link,
            },
            handbooks: {
                states: {
                    multistate: MIGRATED_COLORS.handbookMultistate,
                    federal: MIGRATED_COLORS.handbookFederal,
                    onestate: MIGRATED_COLORS.handbookOnestate,
                },
            },
            backButton: {
                borderColor: branding.secondaryColor,
                color: branding.secondaryColor,
                focusColor: branding.secondaryColor,
                gradientColor: branding.secondaryColor,
                hoverColor: branding.secondaryColor,
                text: secondaryTextColor,
            },
            deleteButton: {
                borderColor: MIGRATED_COLORS.bannerRed,
                color: MIGRATED_COLORS.bannerRed,
                gradientColor: MIGRATED_COLORS.bannerRed,
                text: MIGRATED_COLORS.black,
            },
            actionButton: {
                borderColor: MIGRATED_COLORS.lightblue,
                color: MIGRATED_COLORS.lightblue,
                gradientColor: MIGRATED_COLORS.lightblue,
                text: MIGRATED_COLORS.black,
            },
            cancelButton: {
                borderColor: MIGRATED_COLORS.lightly,
                color: MIGRATED_COLORS.lightest,
                gradientColor: MIGRATED_COLORS.lightest,
                text: MIGRATED_COLORS.black,
            },
            saveButton: {
                borderColor: MIGRATED_COLORS.semanticNewHighlight,
                color: MIGRATED_COLORS.semanticNewHighlight,
                gradientColor: MIGRATED_COLORS.semanticNewHighlight,
                text: MIGRATED_COLORS.black,
            },
            lightSmallAction: {
                text: MIGRATED_COLORS.black,
                gradientColor: 'transparent',
                color: 'transparent',
                borderColor: MIGRATED_COLORS.placeholder,
            },
            tableRowDisabled: {
                color: MIGRATED_COLORS.grey,
            },
            emailPrimaryButton: {
                text: MIGRATED_COLORS.white,
                gradientColor: MIGRATED_COLORS.orange,
                color: darken(0.1, MIGRATED_COLORS.orange),
                borderColor: MIGRATED_COLORS.dark,
                focusColor: MIGRATED_COLORS.white,
            },
            emailSecondaryButton: {
                text: MIGRATED_COLORS.orange,
                gradientColor: MIGRATED_COLORS.white,
                color: darken(0.1, MIGRATED_COLORS.white),
                borderColor: MIGRATED_COLORS.orange,
                focusColor: MIGRATED_COLORS.white,
            },
            primaryPPAButton: {
                text: MIGRATED_COLORS.black,
                gradientColor: MIGRATED_COLORS.blue8,
                color: darken(0.1, MIGRATED_COLORS.blue8),
                borderColor: MIGRATED_COLORS.blue8,
                backgroundColor: MIGRATED_COLORS.blue8,
                disabledBackgroundColor: MIGRATED_COLORS.blue9,
            },
            secondaryPPAButton: {
                text: MIGRATED_COLORS.black,
                gradientColor: MIGRATED_COLORS.lightBanner,
                color: darken(0.1, MIGRATED_COLORS.lightBanner),
                borderColor: MIGRATED_COLORS.lightBanner,
                backgroundColor: MIGRATED_COLORS.lightBanner,
            },
            showMoreButton: {
                text: MIGRATED_COLORS.white,
                gradientColor: MIGRATED_COLORS.grey8,
                color: MIGRATED_COLORS.grey8,
                borderColor: MIGRATED_COLORS.grey8,
            },
            activeCalendar: {
                text: primaryTextColor,
                gradientColor: branding.primaryColor,
                color: darken(0.1, branding.primaryColor),
                borderColor: 'transparent',
                focusColor: 'transparent',
            },
            inactiveCalendar: {
                text: MIGRATED_COLORS.black,
                gradientColor: 'transparent',
                color: darken(0.1, branding.secondaryColor),
                borderColor: 'transparent',
                focusColor: 'transparent',
                hoverColor: branding.secondaryTextColor,
            },
        },
        typography: {
            mapping: {
                documentFilter: 'span',
                documentListSummary: 'span',
                documentListTitle: 'span',
                documentTotalResults: 'span',
                documentTopicTitle: 'h1',
                pageSectionTitle: 'h1',
                documentTypeTitle: 'h3',
                searchSuggestionsTitle: 'h3',
                bodyNew: 'p',
                bodyNewInline: 'span',
                paragraph: 'p',
                link: 'a',
                pBody: 'p',
                pSmallBody: 'p',
                favTertiarySubtitle: 'h4',
                robSubtitle: 'h3',
                robSuperSmallHeader: 'h6',
                heading1: 'h1',
                pNormal: 'p',
            },
            root: {
                fontFamily: `Favorit, Arial, Helvetica, sans-serif`,
                fontSize: '1rem',
                fontWeight: 400,
                margin: '5px 0',
            },
            headline: {
                fontFamily: 'Roboto',
                fontSize: '1.375rem',
                fontWeight: 500,
                lineHeight: '1.3',
                letterSpacing: '.02em',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1rem',
                },
            },
            dashboardWidgetHeader: {
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontWeight: 500,
                letterSpacing: '0.93px',
                color: MIGRATED_COLORS.black,
            },
            display1: {
                fontSize: '1.125rem',
                fontWeight: 500,
                lineHeight: '1.2',
            },
            sidebarHelpText: {
                fontSize: '13px',
                padding: '10px',
            },
            sidebarOption: {
                fontSize: '12px',
                textTransform: 'uppercase',
                margin: 0,
            },
            handbookCardName: {
                fontSize: '18px',
            },
            handbookCardCompanyName: {
                fontSize: '14px',
                fontWeight: 300,
            },
            handbookCardStateInfoTitle: {
                fontFamily: 'Roboto',
                fontSize: '14px',
                lineHeight: 1,
            },
            handbookCardStateInfoText: {
                fontSize: '12px',
                lineHeight: 1,
            },
            // **********************************************************
            // ** Following is the Typography styles as per the new UX **
            // **********************************************************
            h1: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '2.25rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: 'normal',
                letterSpacing: 'normal',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.875rem',
                },
            },
            h1Article: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '2rem',
                fontWeight: '400',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: '2.563rem',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.5rem',
                },
            },
            quote: {
                color: MIGRATED_COLORS.placeholder,
                fontSize: '2.375rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            h2: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '1.625rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.375rem',
                },
            },
            h2Article: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '1.25rem',
                fontWeight: '500',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: '1.688rem',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1rem',
                },
            },
            h3: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '1.375rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.125rem',
                },
            },
            h4: {
                display: 'block',
                fontSize: '1.125rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                marginBottom: SPACING.M,
                marginTop: '0',
                color: MIGRATED_COLORS.black,
            },
            h6: {
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: '0.938rem',
            },
            subheader: {
                color: MIGRATED_COLORS.black3,
                display: 'block',
                fontFamily: 'Roboto',
                fontSize: '1.25rem',
                fontWeight: '300',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            navMain: {
                color: MIGRATED_COLORS.black,
                fontSize: '1.125rem',
                fontWeight: 'bold',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            navDropdown: {
                color: MIGRATED_COLORS.black,
                letterSpacing: 'normal',
                lineHeight: 'normal',
                fontFamily: 'Roboto',
                fontSize: '0.9375rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
            },
            navSubnav: {
                color: MIGRATED_COLORS.darkest,
                fontSize: '1rem',
                fontWeight: 500,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            searchBarActive: {
                color: MIGRATED_COLORS.darkest,
                fontSize: '1rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            bodyNew: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '1rem',
                fontWeight: 300,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 1.25,
            },
            bodyNewInline: {
                display: 'inline',
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '1rem',
                fontWeight: 300,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 1.25,
            },
            showAll: {
                color: MIGRATED_COLORS.tan,
                cursor: 'pointer',
                fontSize: '0.938rem',
                fontWeight: 600,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            showAllDocuments: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                cursor: 'pointer',
                fontFamily: 'RobotoMono',
                fontSize: '0.875rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: '500',
                height: '1rem',
                letterSpacing: 'normal',
                lineHeight: '1.14',
                textDecoration: 'underline',
            },
            navLink: {
                color: MIGRATED_COLORS.white,
                fontSize: '0.938rem',
                fontWeight: 500,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            navLinkHover: {
                color: MIGRATED_COLORS.white,
                fontFamily: 'Favorit, Arial, Helvetica, sans-serif',
                fontSize: '0.938rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            selection: {
                color: MIGRATED_COLORS.darkest,
                fontSize: '0.875rem',
                fontWeight: 500,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            imageDescription: {
                color: MIGRATED_COLORS.darkest,
                fontSize: '0.875rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                textAlign: 'center',
            },
            tooltip: {
                color: MIGRATED_COLORS.darkest,
                fontSize: '0.688rem',
                fontWeight: 500,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            breadCrumbItem: {
                color: MIGRATED_COLORS.black2,
                cursor: 'default !important',
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontWeight: 'normal',
                margin: 0,
            },
            breadCrumbLink: {
                color: MIGRATED_COLORS.link,
                cursor: 'pointer',
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontWeight: 'normal',
                margin: 0,
            },
            breadCrumbLinkActive: {
                color: MIGRATED_COLORS.black,
                cursor: 'pointer',
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontWeight: 'normal',
                margin: 0,
            },
            documentListTitle: {
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                cursor: 'pointer',
                fontSize: '1rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'medium',
                letterSpacing: 'normal',
                lineHeight: '1.25rem',
            },
            documentListSummary: {
                color: MIGRATED_COLORS.black,
                fontSize: '1rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 300,
                letterSpacing: 'normal',
                lineHeight: '1.25rem',
            },
            documentFilter: {
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 500,
                letterSpacing: 'normal',
                lineHeight: 'normal',
                margin: 0,
            },
            documentCheckboxFilter: {
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '0.813rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 300,
                letterSpacing: 'normal',
                lineHeight: 1.46,
                margin: '0 0 0 0.813rem',
            },
            documentResultsBottom: {
                color: MIGRATED_COLORS.black,
                display: 'block',
                fontFamily: 'RobotoMono',
                fontSize: '0.75rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 300,
                letterSpacing: 'normal',
                lineHeight: 'normal',
                margin: `0 0 ${SPACING.M} 0`,
            },
            documentResultsMobile: {
                color: MIGRATED_COLORS.black,
                display: 'block',
                fontFamily: 'RobotoMono',
                fontSize: '0.875rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 300,
                letterSpacing: 'normal',
                lineHeight: 'normal',
                margin: 0,
            },
            documentResultsTop: {
                color: MIGRATED_COLORS.placeholder,
                display: 'block',
                fontFamily: 'Roboto',
                fontSize: '1rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                margin: 0,
            },
            gotoPage: {
                color: MIGRATED_COLORS.black,
                fontSize: '0.813rem',
                fontFamily: 'RobotoMono',
            },
            paginationList: {
                color: MIGRATED_COLORS.dark,
                fontFamily: 'RobotoMono',
                fontSize: '1rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 500,
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            paginationListActive: {
                color: MIGRATED_COLORS.black,
                fontFamily: 'RobotoMono',
                fontSize: '1rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 500,
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            paginationResults: {
                fontFamily: 'RobotoMono',
                textTransform: 'uppercase',
            },
            paginationMobileActions: {
                fontFamily: 'RobotoMono',
                fontWeight: 'bold',
                color: MIGRATED_COLORS.black2,
            },
            paginationMobileLabels: {
                fontFamily: 'RobotoMono',
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                color: MIGRATED_COLORS.black,
                fontWeight: 500,
            },
            backButton: {
                color: branding.secondaryTextColor,
                fontSize: '1rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                margin: `0 0 0 ${SPACING.M}`,
            },
            noNewNotifications: {
                fontSize: '0.75rem',
                fontWeight: '300',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: '1.5',
                letterSpacing: 'normal',
                color: MIGRATED_COLORS.black,
            },
            viewAllNotifications: {
                fontSize: '0.875rem',
                fontWeight: '500',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: '1.29',
                letterSpacing: 'normal',
                color: MIGRATED_COLORS.tan,
            },
            notificationMenuButtons: {
                fontSize: '0.813rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: '1.38',
                letterSpacing: 'normal',
                color: MIGRATED_COLORS.tan,
            },
            notificationItemMessage: {
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontWeight: '300',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: '1',
                letterSpacing: 'normal',
            },
            modalTitle: {
                display: 'block',
                fontFamily: 'Roboto',
                fontSize: '1.375rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            modalDescription: {
                color: MIGRATED_COLORS.black,
                fontSize: '1rem',
                fontWeight: 300,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 1.25,
            },
            documentTopicTitle: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '3rem',
                lineHeight: 'normal',
                margin: `0`,
                padding: `0 ${SPACING.XL}`,
            },
            documentTypeTitle: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '1.375rem',
                lineHeight: 'normal',
                margin: 0,
                padding: 0,
            },
            searchSuggestionsTitle: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontFamily: `Favorit`,
                fontSize: '1.375rem',
                lineHeight: 'normal',
                margin: `0 0 1.0625rem`,
                padding: 0,
            },
            link: {
                color: MIGRATED_COLORS.link,
                cursor: 'pointer',
                fontFamily: `Favorit, Arial, Helvetica, sans-serif`,
                fontSize: '0.875rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: '1.29',
                letterSpacing: 'normal',
            },
            link2: {
                fontWeight: '500',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: '1.29',
                letterSpacing: 'normal',
                fontSize: '1rem',
                cursor: 'pointer',
                color: MIGRATED_COLORS.tan,
            },
            linkDisable: {
                color: MIGRATED_COLORS.grey14,
                cursor: 'not-allowed',
                fontFamily: `Favorit, Arial, Helvetica, sans-serif`,
                fontSize: '0.875rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: '1.29',
                letterSpacing: 'normal',
            },
            bottomLinkBar: {
                fontfamily: 'Favorit, Arial, Helvetica, sans-serif',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: 'normal',
                letterSpacing: 'normal',
                fontSize: '0.875rem',
                cursor: 'pointer',
                color: MIGRATED_COLORS.link,
                textDecoration: 'underline',
            },
            pageSectionTitle: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '2.25rem',
                fontFamily: 'Favorit, Arial, Helvetica, sans-serif',
                lineHeight: 'normal',
                margin: `1.125rem 0 ${SPACING.XS} 0`,
                padding: '0',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.75rem',
                },
            },
            pageSectionSubtitle: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '1.75rem',
                lineHeight: 'normal',
                margin: `0`,
                padding: `0`,
            },
            pageSectionSidebarTitle: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '3rem',
                lineHeight: 'normal',
                margin: `1.125rem 0 1.5625rem 1.5rem`,
                padding: `0`,
            },
            textStyle2: {
                color: MIGRATED_COLORS.black,
                fontFamily: 'inherit',
                fontSize: '1rem',
                fontWeight: 300,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 1.25,
            },
            textStyle3: {
                color: MIGRATED_COLORS.black,
                fontFamily: 'Favorit, Arial, Helvetica, sans-serif',
                fontSize: '2.25rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                display: 'flex',
                alignItems: 'center',
            },
            helpDocumentText: {
                fontSize: '1rem',
                color: MIGRATED_COLORS.darkest,
            },
            mostPopularTitle: {
                display: 'block;',
                fontFamily: 'RobotoMono',
                fontSize: '0.75rem',
                textTransform: 'Uppercase',
                color: MIGRATED_COLORS.black,
                margin: `0.8125rem 0 0.875rem 0`,
            },
            QuestionHRFitness: {
                display: 'block',
                fontSize: '1.5rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                color: MIGRATED_COLORS.black,
                marginBottom: '1rem',
                marginTop: '0',
            },
            widgetHeaderTitle: {
                fontSize: '1rem',
                fontWeight: '500',
                color: MIGRATED_COLORS.darkest,
                margin: '0',
            },
            toDoListType: {
                fontFamily: 'RobotoMono',
                fontSize: '0.625rem',
                fontWeight: 'bold',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: '1',
            },
            QuestionnaireQuestion: {
                display: 'block',
                fontSize: '1.5rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                color: MIGRATED_COLORS.black,
                marginBottom: '1rem',
                marginTop: '0',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.125rem',
                },
            },
            SectionModalTitle: {
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                fontStyle: 'normal',
                lineHeight: 'normal',
                color: MIGRATED_COLORS.darkest,
                marginTop: '1.5625rem',
                marginBottom: '0.9375rem',
            },
            tabTitle: {
                fontSize: '0.938rem',
                fontWeight: 'normal',
                color: MIGRATED_COLORS.black,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.375rem',
                },
            },
            viewDocumentLink: {
                fontFamily: 'inherit',
                fontSize: '1rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                textDecoration: 'underline',
                letterSpacing: 'normal',
                cursor: 'pointer',
                color: MIGRATED_COLORS.black,
            },
            tableColActionLink: {
                fontFamily: 'RobotoMono',
                fontSize: '0.875rem',
                fontWeight: 'normal',
                color: MIGRATED_COLORS.black,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: '1.14',
                cursor: 'pointer',
            },
            wizardTitles: {
                fontFamily: 'RobotoMono',
                fontSize: '0.875rem',
                color: MIGRATED_COLORS.black,
                fontWeight: '300',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
            },
            radioListItems: {
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            fitnessResultsBoxTitles: {
                display: 'block',
                fontFamily: 'RobotoMono',
                fontSize: '0.75rem',
                color: MIGRATED_COLORS.black,
                fontWeight: '300',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: '0.56px',
                textTransform: 'uppercase',
            },
            defaultRoboto: {
                fontFamily: 'Roboto',
                fontSize: '1rem',
                color: 'inherit',
                fontWeight: '300',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
            },
            defaultRobotoMono: {
                display: 'block',
                fontFamily: 'RobotoMono',
                fontSize: '0.75rem',
                fontWeight: '300',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: '0.56px',
            },
            QuickProductlink: {
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: 'normal',
                letterSpacing: 'normal',
                color: MIGRATED_COLORS.link,
                cursor: 'pointer',
                margin: '0',
            },
            ButtonActionSmall: {
                fontFamily: `Favorit, Arial, Helvetica, sans-serif`,
                fontSize: '0.75rem',
                color: MIGRATED_COLORS.black,
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
            },
            paragraph: {
                display: 'block',
                fontFamily: 'Roboto',
                fontSize: '1rem',
                fontWeight: 300,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 1.5,
                marginTop: 0,
                marginBottom: '1rem',
            },
            tableActionButtons: {
                fontFamily: `Favorit, Arial, Helvetica, sans-serif`,
                fontSize: '0.813rem',
                textTransform: 'uppercase',
            },
            labelInputs: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '0.875rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
            },
            todoCompletedText: {
                fontFamily: `Helvetica`,
                fontSize: '1rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: '1.5',
                letterSpacing: 'normal',
            },
            brandingTitle: {
                display: 'flex',
                color: MIGRATED_COLORS.black,
                margin: '0 0 1rem',
                fontFamily: `Favorit, Arial, Helvetica, sans-serif`,
                fontSize: '1.625rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: 'normal',
                letterSpacing: 'normal',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.375rem',
                },
            },
            DashboardTitle: {
                display: 'block',
                color: MIGRATED_COLORS.black,
                fontSize: '2.5rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: 'normal',
                letterSpacing: 'normal',
                [`@media only screen and (max-width: ${THEME.breakpoints.values.md}px)`]: {
                    fontSize: '1.875rem',
                },
            },
            todoTabTitle: {
                color: MIGRATED_COLORS.black,
                letterSpacing: 'normal',
                lineHeight: 'normal',
                fontFamily: 'Roboto',
                fontSize: '0.9375rem',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
            },
            intelligenceSummary: {
                fontSize: '1rem',
                fontWeight: '300',
                color: MIGRATED_COLORS.black,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                fontFamily: 'Roboto',
            },
            intelligenceActionTitle: {
                display: 'inline',
                color: MIGRATED_COLORS.black,
                fontFamily: 'Roboto',
                fontSize: '1rem',
                fontWeight: 400,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 1.25,
            },
            intelligenceActionLink: {
                fontfamily: 'Favorit, Arial, Helvetica, sans-serif',
                fontWeight: 'bold',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: 'normal',
                letterSpacing: 'normal',
                fontSize: '1.25rem',
                color: MIGRATED_COLORS.grey4,
            },
            labelErrorValidation: {
                fontSize: '0.75rem',
                fontFamily: 'Roboto',
                fontWeight: '500',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                color: MIGRATED_COLORS.red,
            },
            subNavCaption: {
                fontSize: '0.875rem',
                fontFamily: 'Roboto',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                color: branding.secondaryTextColor,
            },
            insightsTitle: {
                fontStretch: 'normal',
                fontStyle: 'normal',
            },
            insightsAction: {
                fontFamily: 'Roboto',
                lineHeight: '1.33',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
            },
            insightsTodoLink: {
                fontFamily: 'Roboto',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 300,
                fontSize: '1.5rem',
            },
            insightsDashboardActionLink: {
                fontFamily: 'Roboto',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '1rem',
                lineHeight: '1.19',
                color: MIGRATED_COLORS.orange,
                letterSpacing: 'normal',
                cursor: 'pointer',
                display: 'block',
            },
            insightsDashboardTitles: {
                fontFamily: 'Favorit',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '1.5rem',
                color: MIGRATED_COLORS.black,
                letterSpacing: 'normal',
            },
            insightsDashboardSubTitles: {
                fontFamily: 'Favorit',
                fontStretch: 'normal',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '1rem',
                color: MIGRATED_COLORS.black,
                letterSpacing: 'normal',
            },
            intelligenceEditCompanyButton: {
                fontFamily: 'Favorit, Arial, Helvetica, sans-serif',
                fontSize: '16px',
                color: MIGRATED_COLORS.black00,
                letterSpacing: '0.6px',
                lineHeight: '21.44px',
            },
            intelligenceDashboardLink: {
                fontFamily: 'Roboto',
                fontSize: '16px',
                color: MIGRATED_COLORS.link,
                lineHeight: '23px',
                fontWeight: '500',
                textDecoration: 'underline',
            },
            linkBase: {
                fontFamily: `Roboto, Helvetica, Arial sans-serif`,
                color: MIGRATED_COLORS.black,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
            },
            categoriesSearch: {
                fontFamily: `Roboto, Helvetica, Arial sans-serif`,
                fontSize: '0.75rem',
                fontWeight: 500,
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                marginTop: 0,
                marginBottom: 0,
                textTransform: 'uppercase',
            },
            categoryTags: {
                display: 'block',
                fontFamily: 'Roboto',
                fontSize: '0.75rem',
                fontWeight: '400',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: '0.1px',
            },
            favTertiarySubtitle: {
                display: 'block',
                fontFamily: 'Favorit, Arial, Helvetica, sans-serif',
                fontSize: '0.875rem',
                lineHeight: '19px',
                fontWeight: '400',
            },
            robSubtitle: {
                display: 'block',
                fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                fontSize: '1.25rem',
                lineHeight: '27px',
                fontWeight: '400',
            },
            robSuperSmallHeader: {
                display: 'block',
                fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                fontSize: '0.75rem',
                lineHeight: '23px',
                fontWeight: '400',
            },
            pBody: {
                display: 'block',
                fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                fontSize: '1rem',
                lineHeight: '23px',
                fontWeight: '400',
                marginTop: 0,
                marginBottom: '1rem',
            },
            pSmallBody: {
                display: 'block',
                fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                fontSize: '0.875rem',
                lineHeight: '19px',
                fontWeight: '400',
                marginTop: 0,
                marginBottom: '1rem',
            },
            ipmTitle: {
                display: 'block',
                fontSize: '1.125rem',
                fontWeight: 'normal',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
                lineHeight: 'normal',
                marginTop: '0',
            },
            ipmSmallBody: {
                display: 'block',
                fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                fontSize: '0.875rem',
                lineHeight: '19px',
                fontWeight: '400',
                marginTop: 0,
            },
            heading1: {
                display: 'block',
                fontSize: '2rem',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '120%',
            },
            heading5: {
                display: 'block',
                fontSize: '1.25rem',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '120%',
            },
            pNormal: {
                fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                fontSize: '1rem',
                lineHeight: '150%',
                fontWeight: '400',
                marginTop: 0,
                marginBottom: '1rem',
            },
        },
    };
};

export const isValidLogo = file => {
    let isValid = true;
    let error = '';

    if (file && file.type && file.type.indexOf('image') !== -1) {
        if (file.size > BRANDING.LOGOS.MAX_FILE_SIZE) {
            isValid = false;
            error = BRANDING.ERRORS.INVALID_FILE_SIZE;
        }
    } else {
        isValid = false;
        error = BRANDING.ERRORS.INVALID_FILE_TYPE;
    }

    return { isValid, error };
};

export const buildRestoreDefaultDescription = (sectionText = '') => {
    return BRANDING.DESCRIPTIONS.RESTORE_DEFAULT.replace('SECTION_TEXT', sectionText);
};
